import { z } from 'zod';

export const prepaidPolicySchema = z
  .object({
    cardRelease: z.enum(['AUTHORIZED', 'CONFIRMED']),
    partialRelease: z.enum(['FULL', 'PROPORTIONAL']),
    validityMonths: z.union([z.literal(0), z.literal(3), z.literal(6), z.literal(12)]),
    extensionMode: z.enum(['DISABLED', 'CUMULATIVE', 'PER_REQUEST']),
    extensionMonths: z.number().int().min(0).max(24),
    noShowConsumes: z.boolean(),
    lateCancellationHours: z.union([z.literal(0), z.literal(12), z.literal(24), z.literal(48)]),
  })
  .strict();
export type PrepaidPolicy = z.infer<typeof prepaidPolicySchema>;
export type PrepaidOverrides = { [K in keyof PrepaidPolicy]?: PrepaidPolicy[K] | undefined };
export interface PrepaidRules {
  policy: PrepaidPolicy;
  origins: Record<keyof PrepaidPolicy, 'COMPANY' | 'UNIT' | 'PACKAGE'>;
}
export interface PrepaidEvent {
  id: string;
  stockMovementId?: string | null | undefined;
  type: 'RELEASE' | 'USE' | 'UNDO' | 'BONUS' | 'CORRECTION' | 'EXTEND' | 'BLOCK' | 'CLOSE';
  quantity: number;
  reason: string | null;
  reversesId: string | null;
  correctsId?: string | null | undefined;
  reversed?: boolean | undefined;
  unitPositions?: number[] | undefined;
  allocatedCents?: number | undefined;
  businessUnitId: string;
  businessUnitName: string;
  timeZone?: string | undefined;
  actorName: string;
  createdAt: string;
}
export interface PrepaidBalance {
  id: string;
  saleId: string;
  saleItemId: string;
  referenceId?: string | undefined;
  clientId: string;
  clientName?: string | undefined;
  saleNumber: number;
  businessUnitId: string;
  businessUnitName: string;
  timeZone: string;
  packageName: string;
  itemName: string;
  kind: 'SERVICE' | 'PRODUCT';
  quantity: number;
  released: number;
  available: number;
  reserved?: number | undefined;
  used: number;
  unitValuesCents: number[];
  currency: string;
  status: 'AWAITING' | 'AVAILABLE' | 'EXHAUSTED' | 'EXPIRED' | 'BLOCKED' | 'CLOSED';
  releasedOn: string | null;
  expiresOn: string | null;
  extensionMonthsUsed: number;
  rules: PrepaidRules;
  events: PrepaidEvent[];
}
export type PrepaidAction = {
  type: 'USE' | 'UNDO' | 'BONUS' | 'CORRECTION' | 'EXTEND';
  quantity: number;
  locationId?: string | undefined;
  lotId?: string | undefined;
  idempotencyKey?: string | undefined;
  reason?: string | undefined;
  reversesId?: string | undefined;
  correctsId?: string | undefined;
};
export interface PrepaidSettings {
  company: PrepaidPolicy;
  crossUnit: 'SALE_UNIT' | 'ANY_UNIT';
  units: Record<string, PrepaidOverrides>;
}

export interface PrepaidStockOptions {
  locations: Array<{ id: string; displayName: string }>;
  requiresLot: boolean;
  lots: Array<{ id: string; code: string; balance: string; suggested: boolean }>;
}
